/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 * https://github.com/angular/components/tree/master/src/material/checkbox
 */

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  MatCheckboxChange,
  TransitionCheckState,
} from '@angular/material/checkbox';

// Increasing integer for generating unique ids for checkbox components.
let nextUniqueId = 0;

@Component({
  selector: 'app-asuvi-checkbox',
  templateUrl: './asuvi-checkbox.component.html',
  styleUrls: ['./asuvi-checkbox.component.scss'],
})
export class AsuviCheckboxComponent implements OnInit {
  /** The native `<input type="checkbox">` element */
  @ViewChild('checkbox') _elementRef: ElementRef<HTMLElement>;

  /** Event emitted when the checkbox's `checked` value changes. */
  @Output()
  readonly change: EventEmitter<MatCheckboxChange> = new EventEmitter<any>();

  private _uniqueId: string = `mat-checkbox-${++nextUniqueId}`;

  private _currentCheckState: TransitionCheckState = TransitionCheckState.Init;

  private _currentAnimationClass: string = '';

  /** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
  @Input() id: string = this._uniqueId;

  /** Returns the unique id for the visual hidden input. */
  get inputId(): string {
    return `${this.id || this._uniqueId}-input`;
  }

  /** Whether the checkbox is required. */
  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }
  private _required: boolean;

  @Input()
  get color(): string {
    return this._color;
  }
  set color(value: string) {
    if (value) {
      this._color = value;
      // 20% opacity https://gist.github.com/lopspower/03fb1cc0ac9f32ef38f4
      this.rippleColor = `${value}33`;
    }
  }
  private _color: string = '#832ef1';
  public rippleColor: string = '#832ef133';

  /**
   * Whether the checkbox is checked.
   */
  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: boolean) {
    if (value != this.checked) {
      this._checked = value;
      this._changeDetectorRef.markForCheck();
    }
  }
  private _checked: boolean = false;

  /** The value attribute of the native input element */
  @Input() value: string;

  /**
   * Whether the checkbox is disabled. This fully overrides the implementation provided by
   * mixinDisabled, but the mixin is still required because mixinTabIndex requires it.
   */
  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this.disabled) {
      this._disabled = newValue;
      this._changeDetectorRef.markForCheck();
    }
  }
  private _disabled: boolean = false;

  /** Name value will be applied to the input element if present */
  @Input() name: string | null = null;

  // TODO: Get tab index dynamically
  public tabIndex = 0;

  /**
   * Attached to the aria-label attribute of the host element. In most cases, aria-labelledby will
   * take precedence so this may be omitted.
   */
  @Input('aria-label') ariaLabel: string = '';

  /**
   * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
   */
  @Input('aria-labelledby') ariaLabelledby: string | null = null;

  /** The 'aria-describedby' attribute is read after the element's label and field type. */
  @Input('aria-describedby') ariaDescribedby: string;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _ngZone: NgZone
  ) {}

  ngOnInit(): void {}
  _onInteractionEvent(event: Event) {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the `change` output.
    event.stopPropagation();
  }

  /**
   * Event handler for checkbox input element.
   * Toggles checked state if element is not disabled.
   * Do not toggle on (change) event since IE doesn't fire change event when
   *   indeterminate checkbox is clicked.
   * @param event
   */
  _onInputClick(event: Event) {
    // const clickAction = this._options?.clickAction;

    // We have to stop propagation for click events on the visual hidden input element.
    // By default, when a user clicks on a label element, a generated click event will be
    // dispatched on the associated input element. Since we are using a label element as our
    // root container, the click event on the `checkbox` will be executed twice.
    // The real click event will bubble up, and the generated click event also tries to bubble up.
    // This will lead to multiple click events.
    // Preventing bubbling for the second event will solve that issue.
    event.stopPropagation();

    // If resetIndeterminate is false, and the current state is indeterminate, do nothing on click
    if (!this.disabled) {
      // // When user manually click on the checkbox, `indeterminate` is set to false.
      // if (this.indeterminate && clickAction !== 'check') {

      //   Promise.resolve().then(() => {
      //     this._indeterminate = false;
      //     this.indeterminateChange.emit(this._indeterminate);
      //   });
      // }

      this.toggle();
      this._transitionCheckState(
        this._checked
          ? TransitionCheckState.Checked
          : TransitionCheckState.Unchecked
      );

      // Emit our custom change event if the native input emitted one.
      // It is important to only emit it, if the native input triggered one, because
      // we don't want to trigger a change event, when the `checked` variable changes for example.
      this._emitChangeEvent();
    }
  }

  private _transitionCheckState(newState: TransitionCheckState) {
    let oldState = this._currentCheckState;
    let element: HTMLElement = this._elementRef.nativeElement;

    if (oldState === newState) {
      return;
    }
    if (this._currentAnimationClass.length > 0) {
      element.classList.remove(this._currentAnimationClass);
    }

    this._currentAnimationClass = this._getAnimationClassForCheckStateTransition(
      oldState,
      newState
    );
    this._currentCheckState = newState;

    if (this._currentAnimationClass.length > 0) {
      element.classList.add(this._currentAnimationClass);

      // Remove the animation class to avoid animation when the checkbox is moved between containers
      const animationClass = this._currentAnimationClass;

      this._ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          element.classList.remove(animationClass);
        }, 1000);
      });
    }
  }

  private _getAnimationClassForCheckStateTransition(
    oldState: TransitionCheckState,
    newState: TransitionCheckState
  ): string {
    // Don't transition if animations are disabled.
    // if (this._animationMode === 'NoopAnimations') {
    //   return '';
    // }

    let animSuffix: string = '';

    switch (oldState) {
      case TransitionCheckState.Init:
        // Handle edge case where user interacts with checkbox that does not have [(ngModel)] or
        // [checked] bound to it.
        if (newState === TransitionCheckState.Checked) {
          animSuffix = 'unchecked-checked';
        } else if (newState == TransitionCheckState.Indeterminate) {
          animSuffix = 'unchecked-indeterminate';
        } else {
          return '';
        }
        break;
      case TransitionCheckState.Unchecked:
        animSuffix =
          newState === TransitionCheckState.Checked
            ? 'unchecked-checked'
            : 'unchecked-indeterminate';
        break;
      case TransitionCheckState.Checked:
        animSuffix =
          newState === TransitionCheckState.Unchecked
            ? 'checked-unchecked'
            : 'checked-indeterminate';
        break;
      case TransitionCheckState.Indeterminate:
        animSuffix =
          newState === TransitionCheckState.Checked
            ? 'indeterminate-checked'
            : 'indeterminate-unchecked';
        break;
    }

    return `mat-checkbox-anim-${animSuffix}`;
  }

  private _emitChangeEvent() {
    const event: any = {};
    event.source = this;
    event.checked = this.checked;
    this.change.emit(event);
  }

  /** Toggles the `checked` state of the checkbox. */
  toggle(): void {
    this.checked = !this.checked;
  }
}
